import * as React from "react"

const MOBILE_BREAKPOINT = 768
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`

/**
 * Returns whether the viewport is narrower than the mobile breakpoint.
 *
 * Initializes synchronously in the useState lazy initializer so the very first
 * render already has the correct value. Previously the hook started as
 * `undefined`, returned `false`, then flipped in an effect — triggering a
 * second render on every mount of any component that branches on mobile vs
 * desktop (sheets, layout, etc.) plus a brief layout flash as the wrong
 * variant rendered first.
 *
 * Reads `matchMedia(...).matches`, never `window.innerWidth`. Reading
 * innerWidth right after React has committed DOM forces a synchronous layout,
 * once per mounted instance — the public landing page mounts three, and
 * Lighthouse's forced-reflow-insight failed /welcome on it at error level
 * (IN-189, ~35 ms under CI's CPU). A media query answers from the viewport
 * without laying anything out.
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia(MOBILE_QUERY).matches
  })

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(mql.matches)
    mql.addEventListener("change", onChange)
    // Re-check in case the viewport changed between render and subscribe. If
    // unchanged this is a no-op — React bails out of identical setState.
    setIsMobile(mql.matches)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isMobile
}
